/**
 * A render counter, for the assertions the plan asks for by name.
 *
 * > "Assert no node re-renders on pan (React DevTools profiler, or a render counter in the
 * > test build)."
 *
 * DevTools is not available to Playwright, so this is the counter. It is inert unless the
 * page sets `window.__IS_MAP_RENDER_COUNT` before the app loads — a spec does that with
 * `addInitScript`, so nothing counts in a normal session and the flag cannot be turned on by
 * anything a user does.
 *
 * The counts are the only place the map's performance contract is checkable at all. A pan
 * that re-renders 147 nodes is not a bug any functional assertion can see: the map is still
 * correct, just slow, and "slow" does not fail a suite.
 */

export interface RenderCounts {
  /** Per node id. */
  nodes: Record<string, number>;
  /** The whole wire layer, which re-renders when a path CHANGES, not when the camera moves. */
  wires: number;
  /** The stage, which owns the visible set. */
  stage: number;
}

declare global {
  interface Window {
    __IS_MAP_RENDER_COUNT?: boolean;
    __isMapRenders?: RenderCounts;
  }
}

function counts(): RenderCounts | null {
  if (typeof window === 'undefined' || !window.__IS_MAP_RENDER_COUNT) return null;
  if (!window.__isMapRenders) window.__isMapRenders = { nodes: {}, wires: 0, stage: 0 };
  return window.__isMapRenders;
}

export function countNodeRender(id: string): void {
  const c = counts();
  if (c) c.nodes[id] = (c.nodes[id] ?? 0) + 1;
}

export function countWireRender(): void {
  const c = counts();
  if (c) c.wires += 1;
}

export function countStageRender(): void {
  const c = counts();
  if (c) c.stage += 1;
}
